import React, {FC, useCallback, useMemo, useRef, useState} from 'react';
import {Asset, AssetRendition} from '../../../../types.ts';
import {AppDialog} from '@alchemy/phrasea-ui';
import {MemoizedFilePlayer} from '../FilePlayer.tsx';
import {useWindowSize} from '@alchemy/react-hooks/src/useWindowSize.ts';
import {useParams, useLocation} from '@alchemy/navigation';
import {filePlayerRelativeWrapperClassName} from '../Players';
import {Dimensions} from '@alchemy/core';
import {Box, Typography} from '@mui/material';
import FileIntegrations from '../FileIntegrations.tsx';
import {getAsset, getAssets} from '../../../../api/asset.ts';
import {FullPageLoader} from '@alchemy/phrasea-ui';
import RouteDialog from '../../../Dialog/RouteDialog.tsx';
import {getAssetRenditions} from '../../../../api/rendition.ts';
import AssetAttributes from '../AssetAttributes.tsx';
import {useTranslation} from 'react-i18next';
import {useModalFetch} from '../../../../hooks/useModalFetch.ts';
import {useChannelRegistration} from '../../../../lib/pusher.ts';
import {queryClient} from '../../../../lib/query.ts';
import AssetDiscussion from '../AssetDiscussion.tsx';
import {
    AssetAnnotation,
    AssetAnnotationRef,
} from '../Annotations/annotationTypes.ts';
import AssetViewHeader from './AssetViewHeader.tsx';
import {annotationZIndex} from '../Annotations/common.ts';
import {
    AttachmentType,
    MessageFormRef,
} from '../../../Discussion/discussion.ts';
import {useBindAnnotationMessage} from './useBindAnnotationMessage.ts';
import AssetViewInfo from '../AssetViewInfo.tsx';
import StoryCarousel, {storyCarouselHeight} from './StoryCarousel.tsx';
import AssetAppearsIn from '../AssetAppearsIn.tsx';
import AssetAttachments from '../AssetAttachments.tsx';
import {Routing} from '../../../../routes.ts';
import {getMediaBackgroundColor, scrollbarWidth} from '../../../uiVars.ts';
import {NormalizedCollectionResponse} from '@alchemy/api';
import {useTracking} from '@alchemy/phrasea-framework';
import AssetMatomoMetricsView from '../AssetMatomoMetricsView.tsx';
import {FileTypeEnum, getFileTypeFromMIMEType} from '@alchemy/core';

export type IntegrationOverlayCommonProps = {
    dimensions: Dimensions;
};

type IntegrationOverlay<P extends {} = any> = {
    component: FC<P>;
    props: P;
    replace: boolean;
};

export type SetIntegrationOverlayFunction<P extends {} = any> = (
    component: FC<P & IntegrationOverlayCommonProps> | null,
    props?: P,
    replace?: boolean
) => void;

type Props = {};

export default function AssetView({}: Props) {
    const menuWidth = 400;
    const headerHeight = 60;
    let heightRest = headerHeight;
    const {id: assetId, renditionId} = useParams();
    const {state} = useLocation();
    const assetAnnotationsRef: AssetAnnotationRef = useRef(null);
    const messageFormRef: MessageFormRef = useRef(null);
    const previousData = useRef<DataTuple | undefined>();
    const [annotations, setAnnotations] = React.useState<
        AssetAnnotation[] | undefined
    >();
    const {t} = useTranslation();

    const queryKey = ['assets', assetId];
    const [storyAssets, setStoryAssets] =
        React.useState<NormalizedCollectionResponse<Asset>>();
    const [currentStoryAssetId, setCurrentStoryAssetId] = React.useState<
        string | undefined
    >(state?.storyAssetId);

    useChannelRegistration(`asset-${assetId}`, `asset_ingested`, () => {
        queryClient.invalidateQueries({queryKey});
    });

    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const {data, isSuccess, isError} = useModalFetch({
        queryKey,
        staleTime: 2000,
        refetchOnWindowFocus: false,
        queryFn: () =>
            Promise.all([
                getAsset(assetId!),
                getAssetRenditions(assetId!).then(r => r.result),
            ]),
    });

    const winSize = useWindowSize();
    const [integrationOverlay, setIntegrationOverlay] =
        useState<IntegrationOverlay | null>(null);

    const setProxy: SetIntegrationOverlayFunction = useCallback(
        (component, props, replace = false) => {
            if (!component) {
                setIntegrationOverlay(null);
            } else {
                setIntegrationOverlay({
                    component,
                    props,
                    replace,
                });
            }
        },
        [setIntegrationOverlay]
    );

    const [[asset, renditions], rendition] = (
        isSuccess
            ? [
                  data,
                  renditionId !== Routing.UnknownRendition
                      ? data[1].find(r => r.id === renditionId)!
                      : data[1][0],
              ]
            : (previousData.current ?? [[], undefined])
    ) as DataTuple;

    React.useEffect(() => {
        setAnnotations(undefined);
        if (data) {
            previousData.current = [
                data,
                renditionId !== Routing.UnknownRendition
                    ? data[1].find(r => r.id === renditionId)!
                    : data[1][0],
            ];
        }
    }, [data, previousData, renditionId]);

    const isStory = Boolean(asset?.storyCollection);
    if (isStory) {
        heightRest += storyCarouselHeight;
    }

    const dimensions = useMemo<Dimensions>(() => {
        return {
            width: winSize.innerWidth - menuWidth - scrollbarWidth,
            height: winSize.innerHeight - heightRest - 2,
        };
    }, [winSize]);

    React.useEffect(() => {
        if (asset?.storyCollection) {
            getAssets({
                story: asset!.id,
            }).then(setStoryAssets);
        } else {
            setStoryAssets(undefined);
        }
    }, [asset]);

    const {
        onNewAnnotation,
        onUpdateAnnotation,
        onDeleteAnnotation,
        onAttachmentClick,
        onMessageDelete,
        onAttachmentRemove,
        onMessageFocus,
    } = useBindAnnotationMessage({
        assetAnnotationsRef,
        messageFormRef,
    });

    const isImpressionTrackedRef = useRef<string | undefined>();

    const panelHeight = winSize.innerHeight - headerHeight;

    const currentStoryAsset = currentStoryAssetId
        ? storyAssets?.result.find(a => a.id === currentStoryAssetId)
        : undefined;
    const displayedAsset = currentStoryAsset || asset;

    const displayedRenditionFile = currentStoryAsset
        ? currentStoryAsset.main?.file ||
          currentStoryAsset.preview?.file ||
          currentStoryAsset.thumbnail?.file
        : rendition?.file;

    const {trackContentImpression} = useTracking();

    React.useEffect(() => {
        if (asset && isImpressionTrackedRef.current !== asset.id) {
            const type = getFileTypeFromMIMEType(asset?.source?.type);
            if (![FileTypeEnum.Audio, FileTypeEnum.Video].includes(type)) {
                trackContentImpression(asset.resolvedTrackingId, asset.name);
            }

            isImpressionTrackedRef.current = asset.id;
        }
    }, [asset?.id, isImpressionTrackedRef, trackContentImpression]);

    if (!isSuccess && !isError && !previousData.current) {
        return <FullPageLoader />;
    }

    return (
        <RouteDialog>
            {({onClose}) => (
                <AppDialog
                    disableEscapeKeyDown={true}
                    open={true}
                    disablePadding={true}
                    sx={{
                        '.MuiDialogTitle-root': {
                            height: headerHeight,
                            maxHeight: headerHeight,
                            zIndex: annotationZIndex + 10,
                        },
                    }}
                    fullScreen={true}
                    title={
                        !isError ? (
                            <AssetViewHeader
                                asset={asset}
                                rendition={rendition}
                                renditions={renditions}
                                displayActions={!integrationOverlay}
                                currentStoryAsset={currentStoryAsset}
                            />
                        ) : (
                            <></>
                        )
                    }
                    onClose={onClose}
                >
                    {!isSuccess && !isError && <FullPageLoader />}
                    {!isError ? (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div
                                    ref={containerRef}
                                    style={{
                                        height: panelHeight,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width:
                                            dimensions.width + scrollbarWidth,
                                    }}
                                >
                                    <Box
                                        className={
                                            filePlayerRelativeWrapperClassName
                                        }
                                        sx={theme => ({
                                            position: 'relative',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflowY: 'auto',
                                            height: dimensions.height,
                                            width:
                                                dimensions.width +
                                                scrollbarWidth,
                                            maxWidth:
                                                dimensions.width +
                                                scrollbarWidth,
                                            ...getMediaBackgroundColor(theme),
                                        })}
                                    >
                                        {Boolean(displayedRenditionFile) &&
                                            (!integrationOverlay ||
                                                !integrationOverlay.replace) && (
                                                <MemoizedFilePlayer
                                                    assetAnnotationsRef={
                                                        assetAnnotationsRef
                                                    }
                                                    onNewAnnotation={
                                                        onNewAnnotation
                                                    }
                                                    onUpdateAnnotation={
                                                        onUpdateAnnotation
                                                    }
                                                    onDeleteAnnotation={
                                                        onDeleteAnnotation
                                                    }
                                                    annotations={annotations}
                                                    file={
                                                        displayedRenditionFile!
                                                    }
                                                    title={displayedAsset.name}
                                                    trackingId={
                                                        displayedAsset.resolvedTrackingId
                                                    }
                                                    dimensions={dimensions}
                                                    autoPlayable={false}
                                                    controls={true}
                                                    zoomEnabled={true}
                                                />
                                            )}
                                        {integrationOverlay &&
                                            React.createElement(
                                                integrationOverlay.component,
                                                {
                                                    dimensions,
                                                    ...(integrationOverlay.props ||
                                                        {}),
                                                }
                                            )}
                                    </Box>

                                    {isStory ? (
                                        <StoryCarousel
                                            assets={storyAssets}
                                            selectedAsset={displayedAsset}
                                            story={asset}
                                            onAssetClick={a => {
                                                setCurrentStoryAssetId(a.id);
                                            }}
                                        />
                                    ) : null}
                                </div>

                                <Box
                                    sx={theme => ({
                                        width: menuWidth,
                                        maxWidth: menuWidth,
                                        borderLeft: `1px solid ${theme.palette.divider}`,
                                        overflowY: 'auto',
                                        height: panelHeight,
                                    })}
                                >
                                    <AssetAttributes
                                        asset={displayedAsset}
                                        assetAnnotationsRef={
                                            assetAnnotationsRef
                                        }
                                    />

                                    <AssetViewInfo asset={displayedAsset} />
                                    <AssetAppearsIn asset={displayedAsset} />
                                    <AssetMatomoMetricsView
                                        asset={displayedAsset}
                                    />
                                    <AssetAttachments asset={displayedAsset} />

                                    <AssetDiscussion
                                        asset={displayedAsset}
                                        onFocus={onMessageFocus}
                                        onMessageDelete={onMessageDelete}
                                        onAttachmentClick={onAttachmentClick}
                                        onAttachmentRemove={onAttachmentRemove}
                                        normalizeAttachment={a => {
                                            if (
                                                a.type ===
                                                AttachmentType.Annotation
                                            ) {
                                                return {
                                                    ...a,
                                                    data: {
                                                        ...(a.data as AssetAnnotation),
                                                        editable: undefined,
                                                    },
                                                };
                                            }

                                            return a;
                                        }}
                                        messageFormRef={messageFormRef}
                                    />
                                    {displayedRenditionFile ? (
                                        <FileIntegrations
                                            key={displayedRenditionFile.id}
                                            asset={asset}
                                            file={displayedRenditionFile}
                                            assetAnnotationsRef={
                                                assetAnnotationsRef
                                            }
                                            setIntegrationOverlay={setProxy}
                                        />
                                    ) : (
                                        ''
                                    )}
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: dimensions.height,
                            }}
                        >
                            <Typography variant={'h6'}>
                                {t(
                                    'asset.not_found_or_no_access',
                                    'Asset not found or you do not have access to view it.'
                                )}
                            </Typography>
                        </Box>
                    )}
                </AppDialog>
            )}
        </RouteDialog>
    );
}

type DataTuple = [[Asset, AssetRendition[]], AssetRendition];
